import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Users.module.css';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import SearchInput from '../../components/SearchInput/SearchInput';
import Button from '../../components/Button/Button';
import Modal, { ModalDivider, DetailRow } from '../../components/Modal/Modal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import Pagination from '../../components/Pagination/Pagination';
import FilterSelect from '../../components/FilterSelect/FilterSelect';
import Pill from '../../components/Pill/Pill';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import Avatar from '../../components/Avatar/Avatar';
import CoinIcon from '../../icons/CoinIcon';
import { api, mapUser } from '../../lib/api';

const ROLE_PARAM = { Buyer: 'buyer', Seller: 'seller', 'Buyer + Seller': 'both' };
const STATUS_PARAM = { Active: 'active', Inactive: 'suspended' };

const Users = () => {
    const [users, setUsers] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All');
    const [page, setPage] = useState(1);
    const [viewedUser, setViewedUser] = useState(null);
    const [grantUser, setGrantUser] = useState(null);
    const [grantAmount, setGrantAmount] = useState('');
    const [suspendUser, setSuspendUser] = useState(null);
    const [messageUser, setMessageUser] = useState(null);
    const [messageText, setMessageText] = useState('');
    const [messageSending, setMessageSending] = useState(false);
    const [messageError, setMessageError] = useState(null);
    const navigate = useNavigate();

    const loadUsers = useCallback(async () => {
        const params = new URLSearchParams({ page: String(page), limit: '20' });
        if (search.trim()) params.set('search', search.trim());
        if (ROLE_PARAM[roleFilter]) params.set('role', ROLE_PARAM[roleFilter]);
        if (STATUS_PARAM[statusFilter]) params.set('status', STATUS_PARAM[statusFilter]);
        try {
            const data = await api(`/admin/users?${params.toString()}`);
            setUsers(data.users.map(mapUser));
            setTotalPages(Math.max(1, data.pages));
        } catch {
            setUsers([]);
        }
    }, [search, roleFilter, statusFilter, page]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const filteredUsers = users;

    const openGrantModal = (user) => {
        setViewedUser(null);
        setGrantAmount('');
        setGrantUser(user);
    };

    const openSuspendModal = (user) => {
        setViewedUser(null);
        setSuspendUser(user);
    };

    const openMessageModal = (user) => {
        setViewedUser(null);
        setMessageText('');
        setMessageError(null);
        setMessageUser(user);
    };

    // Lands on the thread afterwards, so the admin sees what went and where a
    // reply will appear. Stays open on failure so the message isn't lost.
    const handleSendMessage = async () => {
        setMessageSending(true);
        setMessageError(null);
        try {
            const { conversation } = await api(`/admin/users/${messageUser._id}/message`, {
                method: 'POST',
                body: { text: messageText.trim() },
            });
            navigate(`/support?conversation=${conversation.id}`);
        } catch (err) {
            setMessageError(err.message);
            setMessageSending(false);
        }
    };

    const handleGrantPoints = async () => {
        const amount = Number(grantAmount);
        try {
            await api(`/admin/users/${grantUser._id}/grant-points`, { method: 'POST', body: { points: amount } });
            await loadUsers();
        } catch (err) {
            alert(err.message);
        }
        setGrantUser(null);
    };

    const handleSuspend = async () => {
        try {
            await api(`/admin/users/${suspendUser._id}/suspend`, { method: 'POST' });
            await loadUsers();
        } catch (err) {
            alert(err.message);
        }
        setSuspendUser(null);
    };

    return (
        <div className={`${styles.card} p-4`}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <h2 className={`${styles.cardTitle} mb-0`}>All users</h2>

                <div className="d-flex align-items-center flex-wrap gap-3">
                    <FilterSelect
                        label="User type"
                        value={roleFilter}
                        options={['All', 'Buyer', 'Seller', 'Buyer + Seller']}
                        onChange={setRoleFilter}
                    />
                    <FilterSelect
                        label="Status"
                        value={statusFilter}
                        options={['All', 'Active', 'Inactive']}
                        onChange={setStatusFilter}
                    />
                    <SearchInput
                        placeholder="Search for users..."
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </div>
            </div>

            <div className={`${styles.tableWrap} table-responsive mt-3`}>
                <table className={`${styles.table} w-100`}>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Phone</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Points</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map((user, index) => (
                            <tr key={user._id}>
                                <td className={styles.userId}>{user.id}</td>
                                <td>
                                    <div className="d-flex align-items-center gap-3">
                                        <Avatar name={user.name} index={index} />
                                        <div>
                                            <div className={styles.userName}>{user.name}</div>
                                            {user.farm && <div className={styles.userFarm}>{user.farm}</div>}
                                        </div>
                                    </div>
                                </td>
                                <td>{user.phone}</td>
                                <td>{user.email}</td>
                                <td><Pill>{user.role}</Pill></td>
                                <td>
                                    <span className={`${styles.points} d-inline-flex align-items-center gap-2`}>
                                        <CoinIcon size={16} />
                                        {user.points} points
                                    </span>
                                </td>
                                <td><StatusBadge status={user.status} /></td>
                                <td className="text-end position-relative">
                                    <ActionMenu
                                        ariaLabel="User actions"
                                        items={[
                                            { label: 'View user', onClick: () => setViewedUser(user) },
                                            { label: 'Send message', onClick: () => openMessageModal(user) },
                                            { label: 'Grant points', onClick: () => openGrantModal(user) },
                                            { label: 'Suspend account', danger: true, onClick: () => openSuspendModal(user) },
                                        ]}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination page={page} totalPages={totalPages} onChange={setPage} />

            {viewedUser && (
                <Modal onClose={() => setViewedUser(null)}>
                    <div className="d-flex align-items-center gap-3">
                        <Avatar name={viewedUser.name} index={0} size={72} />
                        <div>
                            <h3 className={`${styles.modalTitle} mb-1`}>{viewedUser.name}</h3>
                            {viewedUser.farm && <div className={styles.userFarm}>{viewedUser.farm}</div>}
                        </div>
                    </div>

                    <div className="mt-3">
                        <StatusBadge status={viewedUser.status} />
                    </div>

                    <ModalDivider />

                    <DetailRow label="Role" className="mb-3"><Pill>{viewedUser.role}</Pill></DetailRow>
                    <DetailRow label="Phone" className="mb-3"><span>{viewedUser.phone}</span></DetailRow>
                    <DetailRow label="Email" className="mb-3"><span>{viewedUser.email}</span></DetailRow>
                    <DetailRow label="Location" className="mb-3"><span>{viewedUser.location}</span></DetailRow>
                    <DetailRow label="Points balance">
                        <span className={`${styles.points} d-inline-flex align-items-center gap-2`}>
                            <CoinIcon size={16} />
                            {viewedUser.points} points
                        </span>
                    </DetailRow>

                    <ModalDivider />

                    <DetailRow label="Listings" className="mb-3"><span>{viewedUser.listings}</span></DetailRow>
                    <DetailRow label="Orders" className="mb-3"><span>{viewedUser.orders}</span></DetailRow>
                    <DetailRow label="Joined"><span>{viewedUser.joined}</span></DetailRow>

                    <ModalDivider />

                    <div className="d-flex gap-3 mt-4">
                        <Button variant="outline-danger" className="flex-fill" onClick={() => openSuspendModal(viewedUser)}>
                            Suspend account
                        </Button>
                        <Button className="flex-fill" onClick={() => openGrantModal(viewedUser)}>
                            Grant points
                        </Button>
                    </div>
                </Modal>
            )}

            {grantUser && (
                <Modal onClose={() => setGrantUser(null)} centered>
                    <h3 className={`${styles.modalTitle} mt-3`}>Enter number of points</h3>

                    <input
                        type="number"
                        min="0"
                        className={`${styles.pointsInput} my-4`}
                        placeholder="0"
                        value={grantAmount}
                        autoFocus
                        onChange={(event) => setGrantAmount(event.target.value)}
                    />

                    <Button
                        className="w-100"
                        disabled={!grantAmount || Number(grantAmount) <= 0}
                        onClick={handleGrantPoints}
                    >
                        Grant points
                    </Button>
                </Modal>
            )}

            {messageUser && (
                <Modal onClose={() => setMessageUser(null)} centered>
                    <h3 className={`${styles.modalTitle} mt-3`}>Message {messageUser.name}</h3>
                    <p className={`${styles.messageHint} mb-0`}>
                        They'll get a notification, and it appears in Support in their app, where they can reply.
                    </p>

                    <textarea
                        className={`${styles.messageInput} my-3`}
                        rows={5}
                        maxLength={2000}
                        placeholder="Write message..."
                        value={messageText}
                        autoFocus
                        onChange={(event) => setMessageText(event.target.value)}
                    />

                    {messageError && <p className={styles.messageError}>{messageError}</p>}

                    <Button
                        className="w-100"
                        disabled={!messageText.trim() || messageSending}
                        onClick={handleSendMessage}
                    >
                        {messageSending ? 'Sending...' : 'Send message'}
                    </Button>
                </Modal>
            )}

            {suspendUser && (
                <ConfirmModal
                    title="Suspend account"
                    confirmLabel="Suspend"
                    onConfirm={handleSuspend}
                    onClose={() => setSuspendUser(null)}
                />
            )}
        </div>
    );
};

export default Users;
